interface CacheEntry {
  size: number;
  lastUsed: number;
  value: string; // base64 of the bytes
}

const META_KEY_SUFFIX = ":__meta";

function bytesToBase64(b: Uint8Array): string {
  let s = "";
  for (const byte of b) {
    s += String.fromCharCode(byte);
  }
  return btoa(s);
}

function base64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

export class SessionCache {
  private namespace: string;
  private maxBytes: number;

  constructor(namespace: string, maxBytes: number) {
    this.namespace = namespace;
    this.maxBytes = maxBytes;
  }

  private metaKey(): string {
    return `${this.namespace}${META_KEY_SUFFIX}`;
  }

  private loadMeta(): Record<string, CacheEntry> {
    const raw = sessionStorage.getItem(this.metaKey());
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed !== "object" || parsed === null) return {};
      return parsed as Record<string, CacheEntry>;
    } catch {
      return {};
    }
  }

  private saveMeta(meta: Record<string, CacheEntry>): void {
    sessionStorage.setItem(this.metaKey(), JSON.stringify(meta));
  }

  get(key: string): Uint8Array | undefined {
    const meta = this.loadMeta();
    const entry = meta[key];
    if (!entry) return undefined;
    entry.lastUsed = Date.now();
    this.saveMeta(meta);
    return base64ToBytes(entry.value);
  }

  set(key: string, bytes: Uint8Array): void {
    const meta = this.loadMeta();
    meta[key] = {
      size: bytes.length,
      lastUsed: Date.now(),
      value: bytesToBase64(bytes),
    };

    // LRU eviction until total ≤ maxBytes
    let total = 0;
    for (const e of Object.values(meta)) {
      total += e.size;
    }
    while (total > this.maxBytes) {
      const lru = Object.entries(meta).sort((a, b) => a[1].lastUsed - b[1].lastUsed)[0];
      if (!lru) break;
      total -= lru[1].size;
      delete meta[lru[0]];
    }
    this.saveMeta(meta);
  }

  clear(): void {
    sessionStorage.removeItem(this.metaKey());
  }
}
